import axios from 'axios';
import { paymentTypeClient } from './paymentTypes.js';

const copy = (value) => (value == null ? null : JSON.parse(JSON.stringify(value)));
const nullable = (value) => (value == null || value === '' ? null : value);

export function snapshotPaymentTerm(term) {
  return term
    ? {
        id: term.id,
        name: term.name,
        type: term.type,
        days: term.type === 'net' ? term.days : null,
        dayOfMonth: term.type === 'monthly' ? (term.dayOfMonth ?? null) : null,
      }
    : null;
}

function calendarDate(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value ? date : null;
}

export function calculateDueDate(invoiceDate, term) {
  const date = calendarDate(invoiceDate);
  if (!date || !term) return null;
  if (term.type === 'net') {
    if (!Number.isSafeInteger(term.days) || term.days < 1) return null;
    date.setUTCDate(date.getUTCDate() + term.days);
  } else if (term.type === 'monthly') {
    if (
      term.dayOfMonth != null &&
      (!Number.isInteger(term.dayOfMonth) || term.dayOfMonth < 1 || term.dayOfMonth > 31)
    )
      return null;
    date.setUTCDate(1);
    date.setUTCMonth(date.getUTCMonth() + 2);
    date.setUTCDate(0);
    date.setUTCDate(Math.min(term.dayOfMonth ?? 31, date.getUTCDate()));
  } else if (term.type !== 'receipt') return null;
  if (!Number.isFinite(date.getTime()) || date.getUTCFullYear() > 9999) return null;
  return date.toISOString().slice(0, 10);
}

export function normalizeInvoiceDetails(input = {}) {
  return {
    invoiceNumber: nullable(input.invoiceNumber),
    paymentTypeId: nullable(input.paymentTypeId),
    invoiceDate: nullable(input.invoiceDate),
    paymentTermId: nullable(input.paymentTermId),
    paymentTermSnapshot: copy(input.paymentTermSnapshot),
    dueDate: nullable(input.dueDate),
    dueDateOverridden: input.dueDateOverridden === true,
  };
}

export function validateInvoiceDetails(input = {}) {
  const errors = {};
  for (const key of ['invoiceDate', 'dueDate']) {
    if (nullable(input[key]) !== null && !calendarDate(input[key]))
      errors[key] = 'Enter a valid date.';
  }
  if (
    input.invoiceNumber != null &&
    (typeof input.invoiceNumber !== 'string' || input.invoiceNumber.trim().length > 100)
  )
    errors.invoiceNumber = 'Use an invoice number of 100 characters or fewer.';
  return errors;
}

export function validatePaymentTerm(term = {}) {
  const errors = {};
  if (!String(term.name || '').trim() || String(term.name).trim().length > 100)
    errors.name = 'Enter a name of 100 characters or fewer.';
  if (!['receipt', 'net', 'monthly'].includes(term.type)) errors.type = 'Choose a term type.';
  if (term.type === 'net' && (!Number.isSafeInteger(term.days) || term.days < 1))
    errors.days = 'Enter a positive whole number of days.';
  if (
    term.type === 'monthly' &&
    term.dayOfMonth != null &&
    (!Number.isInteger(term.dayOfMonth) || term.dayOfMonth < 1 || term.dayOfMonth > 31)
  )
    errors.dayOfMonth = 'Choose a day from 1 to 31 or Last day.';
  if (
    !Array.isArray(term.shopIds) ||
    !term.shopIds.length ||
    term.shopIds.some((id) => !Number.isSafeInteger(Number(id)) || Number(id) <= 0)
  )
    errors.shopIds = 'Choose at least one location.';
  return errors;
}

export const newInvoiceCommandId = () =>
  globalThis.crypto?.randomUUID?.() ||
  `invoice-${Date.now()}-${Math.random().toString(36).slice(2)}`;

// Future endpoints are never called unless the server advertises this capability.
// An unavailable endpoint stays an error.
const liveAdapter = {
  async listTerms({ shopId, includeInactive = false }) {
    return (
      await axios.get(
        route('shop-management.payment-terms.index', { shop_id: shopId ?? route().params.shop_id }),
        {
          params: { includeInactive, scope: shopId == null ? 'all-shops' : 'shop' },
        },
      )
    ).data.rows;
  },
  async saveTerm({ shopId, term, expectedVersion, commandId }) {
    const params = {
      shop_id: shopId ?? route().params.shop_id,
      ...(term.id ? { paymentTerm: term.id } : {}),
    };
    return (
      await axios[term.id ? 'patch' : 'post'](
        route(`shop-management.payment-terms.${term.id ? 'update' : 'store'}`, params),
        { term, expectedVersion, commandId, scope: shopId == null ? 'all-shops' : 'shop' },
      )
    ).data.term;
  },
  async listPaymentTypes({ shopId }) {
    const options = [];
    let page = 1;
    let more = true;
    while (more) {
      const result = await paymentTypeClient.listPaymentTypes({
        shopId,
        active: true,
        page,
        perPage: 50,
      });
      options.push(
        ...result.rows.filter((row) => row.isActive && row.usageTypes.includes('purchase_order')),
      );
      more = result.meta?.has_more === true;
      page += 1;
    }
    return options.map((row) => ({ id: String(row.id), name: row.name }));
  },
  async correctInvoice({ shopId, purchaseId, receiptId, ...input }) {
    return (
      await axios.patch(
        route('shop-management.purchase-orders.receipts.invoice.update', {
          shop_id: shopId,
          purchaseOrder: purchaseId,
          receipt: receiptId,
        }),
        input,
      )
    ).data.receipt;
  },
};

export function createVendorInvoiceTermsContext(payload = {}) {
  const current = () => (typeof payload === 'function' ? payload() : payload) || {};
  return {
    get enabled() {
      return current().enabled === true;
    },
    capabilities: {
      get managePaymentTerms() {
        return current().enabled === true && current().capabilities?.managePaymentTerms === true;
      },
      get editVendorInvoiceDetails() {
        return current().enabled === true && current().capabilities?.editVendorInvoiceDetails === true;
      },
    },
    adapter: liveAdapter,
  };
}
